/* eslint-disable */

import VConsole from 'vconsole';
import { getAppId, getFeishuLogin } from '@/services/ant-design-pro/common';

const _vConsole = new VConsole();

type H5SDK = {
  ready?: (cb: () => void) => void;
  error?: (cb: (err: any) => void) => void;
  config?: (cfg: Record<string, any>) => Promise<void> | void;
  getUserInfo?: (params?: Record<string, any>) => Promise<any> | any;
};

declare global {
  interface Window {
    h5sdk?: H5SDK;
    tt?: any;
  }
  const tt: any;
}

const CDN_URLS = [
  'https://lf1-cdn-tos.bytegoofy.com/goofy/lark/op/h5-js-sdk-1.5.26.js',
  'https://lf-package-cn.feishucdn.com/obj/feishu-static/op/fe/devtools_frontend/remote-debug-0.0.1-alpha.6.js',
];

export const isFeishuUA = () => {
  const ua = navigator.userAgent || '';
  return /Lark|Feishu/i.test(ua);
};

const loadScript = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });

export const ensureSDK = async () => {
  if (window.h5sdk) return;
  let lastErr: any;
  for (const url of CDN_URLS) {
    try {
      console.log(`[FeishuSDK] Loading script: ${url}`);
      await loadScript(url);
      if (window.h5sdk) {
        console.log(`[FeishuSDK] Script loaded successfully: ${url}`);
        return;
      }
    } catch (e) {
      console.error(`[FeishuSDK] Failed to load script: ${url}`, e);
      lastErr = e;
    }
  }
  if (!window.h5sdk && lastErr) throw lastErr;
};

export const getFeishuUser = async (): Promise<API.CurrentUser | undefined> => {
  console.log('[FeishuAuth] Starting getFeishuUser');
  try {
    await ensureSDK();
    const sdk = window.h5sdk as H5SDK;

    if (sdk.error) {
      sdk.error((err) => {
        console.error('[FeishuAuth] h5sdk error:', JSON.stringify(err));
      });
    }

    // 1. Get App ID
    console.log('[FeishuAuth] Step 1: Requesting App ID');
    const appIdRes = await getAppId();
    console.log('[FeishuAuth] Step 1 Result:', appIdRes);

    const appId = appIdRes?.data?.appId;

    if (!appId) {
      console.error('[FeishuAuth] Failed to get App ID. Response:', appIdRes);
      throw new Error('Failed to get App ID');
    }
    console.log('[FeishuAuth] App ID obtained:', appId);

    // 2. SDK Ready
    console.log('[FeishuAuth] Step 2: Waiting for SDK Ready');
    await new Promise<void>((resolve) => {
      if (typeof sdk.ready === 'function') {
        try {
          sdk.ready(() => {
            console.log('[FeishuAuth] SDK Ready callback fired');
            resolve();
          });
        } catch (e) {
          console.warn('[FeishuAuth] SDK Ready call threw error', e);
          resolve();
        }
      } else {
        console.log(
          '[FeishuAuth] SDK ready function not found, assuming ready',
        );
        resolve();
      }
    });
    console.log('[FeishuAuth] SDK Ready completed');

    // 3. Request Access
    console.log('[FeishuAuth] Step 3: Requesting Access (tt.requestAccess)');
    console.log(
      '[FeishuAuth] Current URL for Redirect URI check:',
      window.location.href,
    );

    const code = await new Promise<string>((resolve, reject) => {
      const ttObj = window.tt || (window as any).tt;
      if (ttObj?.requestAccess) {
        ttObj.requestAccess({
          appID: appId,
          scopeList: [],
          success(res: any) {
            console.log('[FeishuAuth] tt.requestAccess success:', res);
            resolve(res.code);
          },
          fail(err: any) {
            console.error(
              '[FeishuAuth] tt.requestAccess failed:',
              JSON.stringify(err),
            );
            reject(err);
          },
        });
      } else {
        console.error('[FeishuAuth] tt.requestAccess is not available');
        reject(new Error('tt.requestAccess is not available'));
      }
    });
    console.log('[FeishuAuth] Auth code obtained:', code);

    // 4. Callback to get user info and token
    console.log('[FeishuAuth] Step 4: Exchanging code for token');
    const loginRes = await getFeishuLogin({ code });
    console.log('[FeishuAuth] login response:', loginRes);

    const token = loginRes?.data?.token;

    if (token) {
      localStorage.setItem('token', token);
      console.log('[FeishuAuth] Token stored in localStorage');
    } else {
      console.error('[FeishuAuth] Failed to get token');
    }

    // Since we don't have user info endpoint yet, return a mock or minimal user
    const current: API.CurrentUser = {
      name: '飞书用户',
      avatar:
        'https://sf1-ttcdn-tos.pstatp.com/obj/eden-cn/ils34mtihn/feishu-logo.png',
      userid: 'feishu_user',
      access: 'admin', // Assume admin for now or adjust as needed
    };
    console.log('[FeishuAuth] User info parsed:', current);

    return current;
  } catch (e) {
    console.error('[FeishuAuth] Feishu login failed with error:', e);
    // Return undefined or throw depending on how we want to handle failures
    // For now, return a fallback user so the app doesn't crash, but auth might fail
    return {
      name: '获取失败',
      avatar:
        'https://sf1-ttcdn-tos.pstatp.com/obj/eden-cn/ils34mtihn/feishu-logo.png',
      userid: 'mock_open_id',
    };
  }
};
